import { useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useOutletContext } from "react-router";
import { CalendarDays, Check, ChevronDown, ChevronRight, Menu, Plus, Trash2, X } from "lucide-react";
import { Button as AriaButton, Dialog, DialogTrigger, Popover } from "react-aria-components";
import { useCoShellPortal } from "../../CoShellPortalContext";
import { NotificationBellButton } from "../../components/NotificationMenu";
import { cn } from "@/utils/helpers";
import type { CockpitContext } from "../../CockpitLayout";
import {
  MONTH_NAMES,
  WEEKDAY_SHORT,
  dayMonthShort,
  pillarHue,
  type CalendarPost,
  type CompactDay,
  type ScheduleMonth,
} from "./calendarModel";
import { formatLabel } from "./CalendarPostCard";
import { ChannelMarks, TurnLine } from "./CalendarParts";
import { SettingsMenu } from "./CalendarHeader";
import { CalendarSheet } from "./CalendarSheet";
import "./calendar.css";

const icon44 =
  "grid size-11 shrink-0 place-items-center rounded-pill text-co-text outline-none hover:text-co-text focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent";

/** Two letters of the brand for its mark in the lane: "Fatafeat" reads "Fa". */
function brandMark(name: string): string {
  const letters = name.replace(/[^\p{L}\p{N}]/gu, "");
  return letters.slice(0, 1).toUpperCase() + letters.slice(1, 2).toLowerCase();
}

/**
 * Bar / Top lane · Phone (and the tablet's 68px top bar): docked and
 * transparent, its controls floating as chrome-frost circles and capsules. The
 * menu; the brand and "Calendar" in one capsule, which on a phone carries a
 * chevron and opens the views menu (Pen "Decide · A", Emad 3 Oct); then the
 * page's actions in one capsule (phone: the bell and +; tablet: +, the bell and
 * settings). M7's corner floats after it on its own. It takes the compact
 * shell's 56px lane, so the shell's floating menu button steps aside.
 */
export function CalendarTopLane({
  brandName,
  onAdd,
  addLabel = "Add a post",
  settings = [],
  views,
  bell,
  tablet = false,
}: {
  brandName?: string | null;
  onAdd?: () => void;
  addLabel?: string;
  settings?: { label: string; onPress: () => void }[];
  /** The phone's views menu under the title capsule: List, Month (Schedule waits on its route). */
  views?: { value: string; options: { label: string; value: string }[]; onChange: (value: string) => void } | null;
  /** The notification bell, a bare 44px button the lane wraps in its capsule. */
  bell?: ReactNode;
  tablet?: boolean;
}) {
  const cockpit = useOutletContext<CockpitContext | undefined>();
  const portal = useCoShellPortal();
  const titleContent = (
    <>
      {brandName ? (
        <span aria-hidden="true" className="grid size-7 shrink-0 place-items-center rounded-full border border-co-line2 text-[12.5px] text-co-text">
          {brandMark(brandName)}
        </span>
      ) : null}
      <span className="min-w-0 truncate text-[15px] font-semibold text-co-text">Calendar</span>
    </>
  );
  const menuItems = views && !tablet ? views.options : [];
  return (
    <div className={cn("co-cal-toplane co-cal-lane", tablet && "co-cal-lane--tablet")}>
      <button
        type="button"
        aria-label="Open navigation"
        disabled={!cockpit}
        onClick={(event) => cockpit?.openNav(event.currentTarget)}
        className="co-cal-lane__glass co-cal-lane__btn"
      >
        <Menu className="size-5 text-co-text2" aria-hidden="true" />
      </button>
      {menuItems.length > 0 && views ? (
        <DialogTrigger>
          <AriaButton aria-label={`Calendar, ${views.value} view. Change the view`} className="co-cal-lane__glass co-cal-lane__title">
            {titleContent}
            <ChevronDown className="size-4 shrink-0 text-co-text2" aria-hidden="true" />
          </AriaButton>
          <Popover placement="bottom start" offset={4} UNSTABLE_portalContainer={portal ?? undefined} className="co-cal-peek z-[var(--z-index-popover)] outline-none">
            <Dialog aria-label="Calendar views" className="co-glass-chrome w-[220px] rounded-card p-1.5 outline-none">
              {({ close }) => (
                <>
                  {menuItems.map((option) => {
                    const on = option.value === views.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={on}
                        onClick={() => {
                          views.onChange(option.value);
                          close();
                        }}
                        className={cn(
                          "flex h-11 w-full items-center justify-between gap-2.5 rounded-row px-2.5 text-left text-[13px] font-medium text-co-text outline-none hover:bg-co-hover focus-visible:bg-co-hover",
                          on && "bg-co-accent-dim hover:bg-co-accent-dim",
                        )}
                      >
                        {option.label}
                        {on ? <Check className="size-3 text-co-accent" strokeWidth={2.25} aria-hidden="true" /> : null}
                      </button>
                    );
                  })}
                  {settings.length > 0 ? <span aria-hidden="true" className="my-1 block h-px" /> : null}
                  {settings.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => {
                        close();
                        item.onPress();
                      }}
                      className="flex h-11 w-full items-center rounded-row px-2.5 text-left text-[13px] font-medium text-co-muted outline-none hover:bg-co-hover hover:text-co-text focus-visible:bg-co-hover"
                    >
                      {item.label}
                    </button>
                  ))}
                </>
              )}
            </Dialog>
          </Popover>
        </DialogTrigger>
      ) : (
        <span className="co-cal-lane__glass co-cal-lane__title">{titleContent}</span>
      )}
      <span className="flex-1" />
      <span className="co-cal-lane__glass co-cal-lane__end">
        {tablet && onAdd ? (
          <button type="button" aria-label={addLabel} onClick={onAdd} className="co-cal-lane__btn">
            <Plus className="size-5 text-co-text2" aria-hidden="true" />
          </button>
        ) : null}
        {bell ?? <NotificationBellButton className="co-cal-lane__btn" />}
        {!tablet && onAdd ? (
          <button type="button" aria-label={addLabel} onClick={onAdd} className="co-cal-lane__btn">
            <Plus className="size-5 text-co-text2" aria-hidden="true" />
          </button>
        ) : null}
        {tablet && settings.length > 0 ? <SettingsMenu items={settings} triggerClassName="co-cal-lane__btn !bg-transparent !shadow-none" /> : null}
      </span>
    </div>
  );
}

/** PhoneLargeTitle: "December ▾" in the display face, opening the month picker. */
export function PhoneLargeTitle({ title, onPick }: { title: string; onPick?: () => void }) {
  if (!onPick) {
    return <h1 className="font-display text-[34px] font-bold leading-[1.15] tracking-normal text-co-text">{title}</h1>;
  }
  return (
    <h1 className="flex">
      <button
        type="button"
        onClick={onPick}
        aria-label={`${title}. Choose a month`}
        className="inline-flex items-center gap-2 font-display text-[34px] font-bold leading-[1.15] tracking-normal text-co-text outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
      >
        {title}
        <ChevronDown className="size-5 text-co-text2" strokeWidth={2} aria-hidden="true" />
      </button>
    </h1>
  );
}

/** Long-press opens a post's actions on a phone; the ⋯ is the visible way in. */
function useLongPress(onLongPress: (() => void) | undefined) {
  const timer = useRef<number | null>(null);
  const fired = useRef(false);
  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };
  /** True once, right after a long-press fired, so the click it ends in is dropped. */
  const consumed = () => {
    const was = fired.current;
    fired.current = false;
    return was;
  };
  if (!onLongPress) return { handlers: {}, consumed };
  return {
    consumed,
    handlers: {
      onPointerDown: (event: ReactPointerEvent) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        fired.current = false;
        cancel();
        timer.current = window.setTimeout(() => {
          fired.current = true;
          onLongPress();
        }, 500);
      },
      onPointerUp: cancel,
      onPointerLeave: cancel,
      onPointerCancel: cancel,
      onPointerMove: (event: ReactPointerEvent) => {
        if (Math.abs(event.movementX) + Math.abs(event.movementY) > 6) cancel();
      },
      onContextMenu: (event: ReactMouseEvent) => event.preventDefault(),
    },
  };
}

/**
 * PhonePostCard: a post in the schedule. The pillar's 3px edge, the title over
 * two lines, the format with its pillar and the channel marks, then whose turn
 * it is. Tapping opens the post; long-press or ⋯ opens its actions.
 */
export function PhonePostCard({
  post,
  quiet = false,
  active = false,
  onOpen,
  onActions,
}: {
  post: CalendarPost;
  /** A past day or a closed month: the text goes quiet. */
  quiet?: boolean;
  active?: boolean;
  onOpen: (anchor: HTMLElement) => void;
  onActions?: () => void;
}) {
  const { slot, state } = post;
  const hue = pillarHue(post.pillarIndex);
  const press = useLongPress(onActions);
  const meta = [formatLabel(slot), slot.pillarName ? slot.pillarName.toLowerCase() : null].filter(Boolean).join(" · ");
  return (
    <div
      role="button"
      tabIndex={0}
      data-post-id={slot.id}
      aria-label={`${slot.title}. ${meta}. ${state.turn.label}.${post.posted ? " Posted." : ""}${onActions ? " Press and hold for its actions." : ""}`}
      {...press.handlers}
      onClick={(event) => {
        if (press.consumed()) return;
        onOpen(event.currentTarget);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(event.currentTarget);
        }
      }}
      className={cn(
        // Calendar / Post card · Phone: pad 12/14, gap 6, radius 12, no hairline; the pillar a 3px edge.
        "relative flex min-w-0 cursor-pointer select-none flex-col gap-1.5 overflow-hidden rounded-card bg-co-surface px-3.5 py-3 text-left outline-none",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-co-accent",
        active && "shadow-[inset_0_0_0_2px_var(--co-accent)]",
      )}
    >
      {hue ? <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-[3px]" style={{ background: hue }} /> : null}
      <span className={cn("text-[15px] font-semibold leading-[1.35]", quiet ? "text-co-text2" : "text-co-text")}>{slot.title}</span>
      <span className="flex min-w-0 items-start gap-2">
        <span className="min-w-0 flex-1 text-[12.5px] leading-[1.5] text-co-muted">{meta}</span>
        <ChannelMarks channels={post.channels} size="md" className="mt-0.5 gap-2" />
      </span>
      <TurnLine owner={state.turn.owner} label={state.turn.label} alert={state.turn.alert} className="items-center text-[12.5px]" />
    </div>
  );
}

/** PhoneDayAnchor: the day's weekday over its numeral, today in the accent circle. */
export function PhoneDayAnchor({ day, weekday, today }: { day: number; weekday: number; today: boolean }) {
  // Calendar / Day anchor · Phone: 44 wide, the weekday over Barlow 26.
  return (
    <span className="flex w-11 shrink-0 flex-col items-center gap-0.5">
      <span className={cn("text-[12.5px] leading-[17px]", today ? "text-co-accent-ink" : "text-co-muted")}>{WEEKDAY_SHORT[weekday]}</span>
      <span
        className={cn(
          "grid place-items-center font-section text-[26px] font-bold leading-none tabular-nums",
          today ? "size-[30px] rounded-full bg-co-accent-fill text-[18px] text-co-on-accent" : "text-co-text",
        )}
      >
        {day}
      </span>
    </span>
  );
}

/** PhoneMonthBanner: a month's name, dividing the schedule where the months turn. */
export function PhoneMonthBanner({ label }: { label: string }) {
  return (
    <h2 className="px-1 pb-3 pt-[18px] font-section text-[20px] font-bold leading-[1.2] text-co-text shadow-[inset_0_1px_0_var(--co-line)]">
      {label}
    </h2>
  );
}

/**
 * The phone's List (and the tablet's): one continuous schedule, each day an
 * anchor beside its posts, months divided by their banners, and "Earlier" at the
 * top for the months before the start.
 */
export function PhoneSchedule({
  months,
  bannerFor,
  quiet,
  activePostId = null,
  earlier = 0,
  onEarlier,
  onOpen,
  actionsFor,
}: {
  months: ScheduleMonth[];
  /** The banner over a month, or null for none (the large title already names it). */
  bannerFor: (month: ScheduleMonth, index: number) => string | null;
  quiet: (post: CalendarPost) => boolean;
  activePostId?: string | null;
  /** Posts before the start. */
  earlier?: number;
  onEarlier?: () => void;
  onOpen: (post: CalendarPost, anchor: HTMLElement) => void;
  /** The post's actions sheet, when it has any. */
  actionsFor?: (post: CalendarPost) => (() => void) | undefined;
}) {
  return (
    // The schedule: pad 4/16/0/12 under a hairline, days 4 apart.
    <div className="-mx-3 flex min-w-0 flex-col gap-1 pl-3 pr-4 pt-1 shadow-[inset_0_1px_0_var(--co-line)]">
      {earlier > 0 && onEarlier ? (
        <button
          type="button"
          onClick={onEarlier}
          className="mb-2 inline-flex h-11 items-center self-center px-4 text-[14px] text-co-text2 outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
        >
          Earlier
        </button>
      ) : null}
      {months.map((group, index) => {
        const banner = bannerFor(group, index);
        return (
          <section key={group.key} aria-label={`${MONTH_NAMES[group.month - 1]} ${group.year}`} className="flex flex-col">
            {banner ? <PhoneMonthBanner label={banner} /> : null}
            <div role="list" className="flex flex-col">
              {group.days.map((day) => (
                <div role="listitem" key={day.key} className="flex gap-3 pb-1.5 pt-2.5">
                  <PhoneDayAnchor day={day.day} weekday={day.weekday} today={day.today} />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    {day.posts.length === 0 ? (
                      <p className="flex min-h-11 items-center text-[13px] text-co-muted">
                        Nothing today
                      </p>
                    ) : (
                      day.posts.map((post) => (
                        <PhonePostCard
                          key={post.slot.id}
                          post={post}
                          quiet={quiet(post)}
                          active={activePostId === post.slot.id}
                          onOpen={(anchor) => onOpen(post, anchor)}
                          onActions={actionsFor?.(post)}
                        />
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/** The schedule while the month loads: rows shaped like it (qRBmx). */
export function PhoneScheduleSkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col">
      {Array.from({ length: 5 }, (_, index) => (
        <div key={index} className="flex gap-3 py-2.5">
          <span className="flex w-11 shrink-0 flex-col items-center gap-2 pt-3">
            <span className="co-cal-skeleton h-2.5 w-6" />
            <span className="co-cal-skeleton size-6 !rounded-full" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-2.5 rounded-card border border-co-line bg-co-surface px-3.5 py-4">
            <span className="co-cal-skeleton h-3 w-[92%]" />
            <span className="co-cal-skeleton h-3 w-[64%]" />
            <span className="co-cal-skeleton h-2.5 w-[40%]" />
            <span className="co-cal-skeleton h-2.5 w-[48%]" />
          </span>
        </div>
      ))}
    </div>
  );
}

const HEADS = ["S", "M", "T", "W", "T", "F", "S"];

function CompactDot({ day }: { day: CompactDay }) {
  if (!day.group) return <span aria-hidden="true" className="size-[5px]" />;
  if (day.posted) return <span aria-hidden="true" className="size-[5px] rounded-full bg-co-live" />;
  if (day.group === "client") {
    return <span aria-hidden="true" className="size-[5px] rounded-full shadow-[inset_0_0_0_1.2px_var(--co-text2)]" />;
  }
  return (
    <span
      aria-hidden="true"
      className="size-[5px] rounded-full"
      style={{ background: day.group === "you" ? "var(--co-accent-ink)" : "var(--co-muted)" }}
    />
  );
}

/**
 * CompactMonth: the phone's month as six weeks of seven cells, each a date and
 * a dot in the whose-turn colour. Today is the accent circle, the chosen day
 * carries the accent outline, and days it cannot take are left plain.
 */
export function CompactMonth({
  days,
  label,
  onChoose,
  allowed,
}: {
  days: CompactDay[];
  label: string;
  onChoose: (day: CompactDay) => void;
  /** The move sheet tints the days a post may land on and refuses the rest. */
  allowed?: (day: CompactDay) => boolean;
}) {
  return (
    // Calendar / Compact month: pad 0/0/10 over a hairline.
    <div role="grid" aria-label={label} className="grid grid-cols-7 gap-y-0.5 pb-2.5">
      <div role="row" className="contents">
        {HEADS.map((head, index) => (
          <span role="columnheader" key={`${head}-${index}`} className="pb-1 text-center text-[11px] text-co-muted">
            {head}
          </span>
        ))}
      </div>
      {Array.from({ length: Math.ceil((days.map((d) => d.cell.inMonth).lastIndexOf(true) + 1) / 7) }, (_, week) => (
        <div role="row" key={week} className="contents">
          {days.slice(week * 7, week * 7 + 7).map((day) => {
            const { cell } = day;
            const can = cell.inMonth && (allowed ? allowed(day) : true);
            const tinted = !!allowed && can;
            return (
              <button
                key={cell.key}
                type="button"
                role="gridcell"
                aria-selected={day.chosen}
                aria-label={`${dayMonthShort(cell)}${day.count ? `, ${day.count} ${day.count === 1 ? "post" : "posts"}` : ""}${day.today ? ", today" : ""}`}
                disabled={!can}
                onClick={() => onChoose(day)}
                className={cn(
                  "flex h-11 flex-col items-center justify-center gap-[3px] outline-none",
                  "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
                  tinted && !day.chosen && "bg-co-accent-dim",
                )}
              >
                <span
                  className={cn(
                    // A 30px number; the chosen day a square of accent fill, today an accent ring.
                    "grid size-[30px] place-items-center text-[15px] font-medium tabular-nums leading-none",
                    day.chosen && "bg-co-accent-fill font-bold text-co-on-accent",
                    !day.chosen && day.today && "rounded-full font-bold text-co-text shadow-[inset_0_0_0_1.5px_var(--co-accent)]",
                    !day.chosen && !day.today && (cell.inMonth ? "text-co-text" : "text-co-faint"),
                    !can && cell.inMonth && allowed && "text-co-faint",
                  )}
                >
                  {cell.day}
                </span>
                <CompactDot day={day} />
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** PhoneGroupHeader: "Tue 15 Dec", "Later in December". */
export function PhoneGroupHeader({ children }: { children: ReactNode }) {
  return <h2 className="px-1 pb-1 pt-3.5 font-section text-[17px] font-bold leading-[1.2] text-co-text">{children}</h2>;
}

/** PhoneDayRow: a post under CompactMonth, its day beside it, opening the post. */
export function PhoneDayRow({
  post,
  day,
  weekday,
  quiet = false,
  onOpen,
  onActions,
}: {
  post: CalendarPost;
  day: number;
  weekday: number;
  quiet?: boolean;
  onOpen: (anchor: HTMLElement) => void;
  onActions?: () => void;
}) {
  const { slot, state } = post;
  const press = useLongPress(onActions);
  return (
    <div
      role="button"
      tabIndex={0}
      data-post-id={slot.id}
      aria-label={`${slot.title}. ${formatLabel(slot)}. ${state.turn.label}.`}
      {...press.handlers}
      onClick={(event) => {
        if (press.consumed()) return;
        onOpen(event.currentTarget);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(event.currentTarget);
        }
      }}
      // Calendar / Day row · Phone: gap 14, pad 12/12/12/16, a hairline under.
      className="-mx-3 flex cursor-pointer select-none items-center gap-3.5 py-3 pl-4 pr-3 shadow-[inset_0_-1px_0_var(--co-line)] outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent"
    >
      <span className="flex w-9 shrink-0 flex-col items-center gap-0.5">
        <span className="font-section text-[24px] font-bold leading-none tabular-nums text-co-text">{day}</span>
        <span className="text-[12.5px] leading-[17px] text-co-muted">{WEEKDAY_SHORT[weekday]}</span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-[5px]">
        <span className={cn("text-[14px] font-semibold leading-[1.35]", quiet ? "text-co-text2" : "text-co-text")}>{slot.title}</span>
        <span className="flex min-w-0 items-center gap-2 text-[12.5px] text-co-muted">
          <span className="truncate">{formatLabel(slot)}</span>
          <ChannelMarks channels={post.channels} size="md" className="gap-2" />
        </span>
        <TurnLine owner={state.turn.owner} label={state.turn.label} alert={state.turn.alert} className="items-center text-[12.5px]" />
      </span>
      <ChevronRight className="size-4 shrink-0 text-co-muted" aria-hidden="true" />
    </div>
  );
}

/**
 * A month state on a phone, in the flow rather than over a grid: not planned
 * (CXTVX), no approved strategy. The card, then its actions as 44px buttons.
 */
export function PhoneStateCard({
  title,
  body,
  counts,
  actions,
}: {
  title: string;
  body: ReactNode;
  /** Calendar / Not planned card · Phone's counts: "1 static", "2 vertical". */
  counts?: { count: number; kind: string }[];
  /** Floated in Bar / Bottom · Phone. */
  actions?: ReactNode;
}) {
  return (
    <div className="-mx-3 flex flex-col gap-3.5 px-4 py-[18px] shadow-[inset_0_1px_0_var(--co-line)]">
      {/* Calendar / Not planned card · Phone: the surface with a line2 hairline, pad 20/18. */}
      <section role="region" aria-label={title} className="flex flex-col gap-3 rounded-card bg-co-surface px-[18px] py-5">
        <h2 className="font-section text-[24px] font-bold leading-[1.2] text-co-text">{title}</h2>
        <div className="text-[15px] leading-[1.5] text-co-text2">{body}</div>
        {counts && counts.length > 0 ? (
          <div className="flex flex-wrap gap-4 py-2.5 shadow-[inset_0_1px_0_var(--co-line)]">
            {counts.map((entry) => (
              <span key={entry.kind} className="flex items-end gap-[5px]">
                <span className="font-section text-[22px] font-bold leading-none text-co-text">{entry.count}</span>
                <span className="text-[13px] text-co-text2">{entry.kind}</span>
              </span>
            ))}
          </div>
        ) : null}
      </section>
      {actions ? <PhoneBottomBar>{actions}</PhoneBottomBar> : null}
    </div>
  );
}

/** Bar / Bottom · Phone: the page's actions floating at the foot, no tray, each 48 tall. */
export function PhoneBottomBar({ children }: { children: ReactNode }) {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 px-2 pb-6 pt-2">
      <div className="pointer-events-auto flex gap-2 p-2 [&>*]:flex-1">{children}</div>
    </div>
  );
}

export const phonePrimary =
  "inline-flex h-12 items-center justify-center gap-2 rounded-pill bg-co-accent-fill px-4 text-[15px] font-semibold text-co-on-accent shadow-[0_8px_24px_var(--co-shell-shadow)] outline-none transition-colors hover:bg-co-accent-fill-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:pointer-events-none disabled:opacity-50";
export const phoneSecondary =
  "co-glass-chrome inline-flex h-12 items-center justify-center gap-2 rounded-pill px-4 text-[15px] font-medium text-co-text outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:pointer-events-none disabled:opacity-50";

/**
 * A post's actions on a phone, from long-press or its ⋯: Move to another day
 * and Delete post, the same choices the peek and the post page carry.
 */
export function PostActionsSheet({
  post,
  onMove,
  onDelete,
  deleteBlocked = false,
  onClose,
}: {
  post: CalendarPost;
  onMove?: () => void;
  onDelete?: () => void;
  deleteBlocked?: boolean;
  onClose: () => void;
}) {
  const row =
    "flex h-12 w-full items-center gap-3 border-b border-co-line px-1 text-left text-[15.5px] outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent disabled:opacity-50";
  return (
    <CalendarSheet phone label={post.slot.title} onClose={onClose}>
      <div className="flex flex-col">
        <div className="flex items-start justify-between gap-2 pb-3">
          <p className="line-clamp-2 min-w-0 pt-2 text-[15px] font-semibold leading-snug text-co-text">{post.slot.title}</p>
          <button type="button" aria-label="Close" onClick={onClose} className={cn(icon44, "text-co-text2")}>
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        <div className="border-t border-co-line">
          {onMove ? (
            <button type="button" onClick={onMove} className={cn(row, "text-co-text")}>
              <CalendarDays className="size-5 text-co-text2" aria-hidden="true" />
              Move to another day
            </button>
          ) : null}
          {onDelete ? (
            <button type="button" disabled={deleteBlocked} onClick={onDelete} className={cn(row, "text-co-alert-ink")}>
              <Trash2 className="size-5" aria-hidden="true" />
              {deleteBlocked ? "Delete post (unavailable while it is working)" : "Delete post"}
            </button>
          ) : null}
        </div>
      </div>
    </CalendarSheet>
  );
}
